// Exact-match FR -> EN dictionary for internal UI (dashboards, JobDetail, components).
// Only whole trimmed text nodes that match a key are translated, so dynamic data
// (names, job titles, free text) is never touched.
export const FR_EN = {
  // Admin nav
  "Tableau de bord": "Dashboard",
  "Offres d'emploi": "Job offers",
  "Candidatures": "Applications",
  "Contrats": "Contracts",
  "Agenda entretiens": "Interview agenda",
  "Utilisateurs": "Users",
  "Messages": "Messages",
  "Salle de réunion": "Meeting room",
  "Enregistrements": "Recordings",
  "Apparence": "Appearance",
  "Déconnexion": "Log out",

  // Admin overview / stats
  "Offres actives": "Active offers",
  "Candidats": "Candidates",
  "En attente": "Pending",
  "Contrats actifs": "Active contracts",
  "Entretiens à venir": "Upcoming interviews",
  "Acceptées": "Accepted",
  "Refusées": "Rejected",

  // Candidate nav / space
  "Reçue / En attente": "Received / Pending",
  "Acceptée": "Accepted",
  "Refusée": "Rejected",
  "Accueil": "Home",
  "Mes postulations": "My applications",
  "Mes entretiens": "My interviews",
  "Contrats obtenus": "Contracts obtained",
  "Mon profil": "My profile",
  "Espace candidat": "Candidate space",
  "Nouvelle candidature": "New application",

  // Candidate home (offers)
  "Offres disponibles": "Available offers",
  "Rechercher une offre...": "Search an offer...",
  "Aucune offre disponible pour le moment.": "No offers available at the moment.",
  "Postuler": "Apply",
  "Voir": "View",
  "Voir le détail": "View details",

  // Candidate applications / screening
  "Vous n'avez pas encore postulé.": "You haven't applied yet.",
  "Parcourir les offres": "Browse offers",
  "Examen de pré-qualification complété": "Pre-qualification exam completed",
  "Un court examen vous attend pour finaliser cette candidature.": "A short exam awaits to finalize this application.",
  "Répondre à l'examen": "Take the exam",
  "Examen de pré-qualification": "Pre-qualification exam",
  "Envoyer mes réponses": "Send my answers",
  "Merci ! Vos réponses ont été transmises au recruteur.": "Thank you! Your answers have been sent to the recruiter.",

  // Common buttons / words
  "Annuler": "Cancel",
  "Supprimer": "Delete",
  "Fermer": "Close",
  "Planifier": "Schedule",
  "Plus tard": "Later",
  "Retour": "Back",
  "Rechercher...": "Search...",
  "Chargement...": "Loading...",
  "Aucun résultat": "No results",

  // Applications (admin)
  "Note interne sur le candidat...": "Internal note about the candidate...",
  "Accepter": "Accept",
  "Refuser": "Reject",
  "Voir le profil complet": "View full profile",
  "Voir l'examen IA": "View AI exam",
  "Planifier l'entretien": "Schedule the interview",
  "Supprimer cette candidature ?": "Delete this application?",
  "Examen de pré-qualification (confidentiel)": "Pre-qualification exam (confidential)",
  "Avis de l'IA": "AI opinion",
  "Toutes": "All",
  "Filtrer par offre": "Filter by offer",

  // Table headers
  "Candidat": "Candidate",
  "Poste": "Position",
  "Statut": "Status",
  "Date": "Date",
  "Actions": "Actions",
  "Nationalité": "Nationality",
  "Rôle": "Role",
  "Appréciation": "Rating",
  "Cand.": "Apps",
  "Titre": "Title",
  "Client": "Client",
  "Montant": "Amount",
  "Entreprise": "Company",
  "Type": "Type",
  "Lieu": "Location",
  "Salaire": "Salary",
  "Catégorie": "Category",

  // Contracts / interviews statuses
  "En cours": "Ongoing",
  "Terminé": "Completed",
  "Résilié": "Terminated",
  "Supprimer ce contrat ?": "Delete this contract?",
  "Supprimer cet entretien ?": "Delete this interview?",
  "Intitulé": "Title",
  "Heure": "Time",
  "Lieu / Lien visio": "Location / video link",
  "Notes": "Notes",

  // Candidates list
  "Rechercher (poste, domaine, nationalité...)": "Search (position, domain, nationality...)",
  "Toute appréciation": "Any rating",
  "(vous)": "(you)",

  // Messages
  "Sélectionnez une conversation": "Select a conversation",
  "En ligne": "Online",
  "Hors ligne": "Offline",
  "Écrire un message...": "Write a message...",

  // Meeting room
  "Démarrer une salle": "Start a room",
  "Inviter": "Invite",
  "Lien copié": "Link copied",

  // Recordings
  "Aucun enregistrement. Lancez un appel ou une réunion et cliquez sur « Enregistrer ».": "No recordings. Start a call or meeting and click \u201cRecord\u201d.",
  "Analyse IA…": "AI analysis…",
  "Prêt": "Ready",
  "Résumé IA": "AI summary",
  "Transcription complète": "Full transcript",
  "Aucune transcription disponible (audio manquant).": "No transcript available (missing audio).",
  "Le résumé IA sera disponible dans quelques instants…": "The AI summary will be available shortly…",
  "Supprimer cet enregistrement ?": "Delete this recording?",
  "La vidéo et son résumé seront supprimés définitivement.": "The video and its summary will be permanently deleted.",

  // Calls
  "Appel entrant": "Incoming call",
  "Appel audio": "Audio call",
  "Appel vidéo": "Video call",
  "Entretien": "Interview",
  "Discuter": "Chat",
  "Arrêter": "Stop",
  "Envoi…": "Uploading…",

  // Profile dialog
  "Administrateur": "Administrator",
  "Entretiens": "Interviews",
  "Années d'exp.": "Years exp.",
  "Expertise": "Expertise",
  "Domaines": "Domains",
  "Outils maîtrisés": "Tools mastered",
  "À propos": "About",
  "Non renseigné": "Not provided",
  "Impossible de charger ce profil.": "Unable to load this profile.",
  "Aucune candidature.": "No applications.",

  // JobDetail (common)
  "Retour aux offres": "Back to offers",
  "Description du poste": "Job description",
  "Profil recherché": "Desired profile",
  "Postuler à cette offre": "Apply to this offer",
  "Déjà postulé": "Already applied",
  "Message vocal (optionnel)": "Voice message (optional)",
  "Lettre de motivation": "Cover letter",
  "Envoyer ma candidature": "Send my application",
  "CV (PDF)": "Resume (PDF)",
  "Vous devez être connecté pour postuler.": "You must be signed in to apply.",

  // Candidate profile banner + form
  "Complétez votre profil": "Complete your profile",
  "Renseignez vos infos et domaines d'expertise : l'IA vous proposera en priorité les offres de votre domaine.": "Fill in your info and areas of expertise: the AI will prioritise offers in your domain.",
  "Compléter": "Complete",
  "Informations personnelles": "Personal information",
  "Nom complet *": "Full name *",
  "Téléphone *": "Phone *",
  "Nationalité *": "Nationality *",
  "Ville": "City",
  "Pays": "Country",
  "Années d'expérience": "Years of experience",
  "Poste actuel": "Current position",
  "Domaines d'expertise * (séparés par des virgules)": "Areas of expertise * (comma separated)",
  "Outils maîtrisés (séparés par des virgules)": "Tools mastered (comma separated)",
  "Titre / accroche": "Title / headline",
  "Enregistrer mon profil": "Save my profile",
  "Ces informations aident notre IA à vous proposer les offres les plus pertinentes.": "This information helps our AI recommend the most relevant offers.",

  // Job type / mode badges
  "Temps plein": "Full-time",
  "Temps partiel": "Part-time",
  "Télétravail": "Remote",
  "Présentiel": "On-site",
  "Hybride": "Hybrid",
  "Freelance": "Freelance",
  "Stage": "Internship",
  "Alternance": "Apprenticeship",
  "CDI": "Permanent",
  "CDD": "Fixed-term",
  "Ingénierie": "Engineering",
  "Ingenierie": "Engineering",

  // Admin toolbars / actions
  "Tous": "All",
  "Nouveau": "New",
  "Examiner": "Review",
  "Démarrez une salle vidéo instantanée, partagez le lien d'invitation, et enregistrez la session pour générer automatiquement un résumé IA (visible dans « Enregistrements »).": "Start an instant video room, share the invite link, and record the session to automatically generate an AI summary (visible in \u201cRecordings\u201d).",
  "Vidéos d'entretiens enregistrées, avec transcription et résumé IA automatiques.": "Recorded interview videos, with automatic transcription and AI summary.",

  // --- Extended sweep (headings, empty states, dialogs) ---
  "Action irréversible.": "Irreversible action.",
  "Cette action est irréversible.": "This action is irreversible.",
  "Action": "Action",
  "Agenda des entretiens": "Interview agenda",
  "Aucun contrat.": "No contracts.",
  "Aucun utilisateur.": "No users.",
  "Aucune conversation.": "No conversations.",
  "Aucune offre publiée.": "No offers published.",
  "Aujourd'hui": "Today",
  "Appliquer": "Apply",
  "Audio": "Audio",
  "Vidéo": "Video",
  "Bouclé": "Completed",
  "Bouclés": "Completed",
  "Résiliés": "Terminated",
  "CV (PDF, DOCX) *": "Resume (PDF, DOCX) *",
  "Candidat accepté :": "Accepted candidate:",
  "Candidature non retenue": "Application not selected",
  "Client / Chef de projet": "Client / Project manager",
  "Connectez-vous pour discuter avec l'administrateur.": "Sign in to chat with the administrator.",
  "Connectez-vous pour postuler.": "Sign in to apply.",
  "Couleur personnalisée": "Custom color",
  "Changez la couleur principale de toute l'application. Thème actuel :": "Change the main color of the whole app. Current theme:",
  "Décision": "Decision",
  "Description": "Description",
  "Domaines détectés par l'IA :": "Domains detected by AI:",
  "Début": "Start",
  "Fin": "End",
  "Détails du profil, expertise et candidatures du candidat.": "Candidate profile details, expertise and applications.",
  "Enregistrer l'évaluation": "Save evaluation",
  "Envoi en cours...": "Uploading...",
  "Export CSV": "Export CSV",
  "Formats acceptés : PDF, DOC, DOCX": "Accepted formats: PDF, DOC, DOCX",
  "Génération...": "Generating...",
  "Générer avec l'IA": "Generate with AI",
  "Pré-remplir avec l'IA": "Pre-fill with AI",
  "Intitulé du poste": "Job title",
  "L'utilisateur et toutes ses candidatures seront supprimés définitivement.": "The user and all their applications will be permanently deleted.",
  "Le support en direct est destiné aux candidats. Utilisez la messagerie de l'administration.": "Live support is for candidates. Use the admin messaging.",
  "Les administrateurs ne peuvent pas postuler.": "Administrators cannot apply.",
  "Liste": "List",
  "Semaine": "Week",
  "Message vocal (facultatif, max 10 min)": "Voice message (optional, max 10 min)",
  "Mon CV": "My resume",
  "Nom": "Name",
  "Note de motivation": "Cover note",
  "Note de motivation (facultatif)": "Cover note (optional)",
  "Offre liée": "Related offer",
  "Profil du candidat": "Candidate profile",
  "Promouvoir admin": "Promote to admin",
  "Rétrograder": "Demote",
  "Publier une offre": "Post an offer",
  "Publier votre première offre": "Post your first offer",
  "Quelques questions rapides pour compléter votre candidature à": "A few quick questions to complete your application to",
  "Recherchez par nom, poste, domaine ou nationalité ; gérez les rôles.": "Search by name, position, domain or nationality; manage roles.",
  "Rejoindre": "Join",
  "Répartition par nationalité": "Distribution by nationality",
  "Se connecter": "Sign in",
  "Suivez son statut depuis votre espace candidat.": "Track its status from your candidate space.",
  "Transcription de votre message vocal": "Transcription of your voice message",
  "Transcription du message vocal": "Voice message transcription",
  "Utilisateurs & rôles": "Users & roles",
  "Visible": "Visible",
  "Voir le CV": "View resume",
  "Voir ma candidature": "View my application",
  "Voir mes candidatures": "View my applications",
  "Voir toutes": "View all",
  "Vous avez déjà postulé à cette offre.": "You have already applied to this offer.",
  "Écouter le vocal": "Play voice",
  "Écrivez à l'administrateur, il vous répondra ici.": "Write to the administrator, they'll reply here.",
  "Évaluation interne (admin)": "Internal evaluation (admin)",
  "Postuler à cette offre": "Apply to this offer",
  "Description du poste": "Job description",
  "Profil recherché": "Desired profile",

  // Placeholders
  "Collez une fiche de poste ou décrivez le poste en quelques mots — l'IA pré-remplit le formulaire.": "Paste a job description or describe the role in a few words — the AI pre-fills the form.",
  "Ex : Développeur Full-Stack": "e.g. Full-Stack Developer",
  "Ex : Entretien technique": "e.g. Technical interview",
  "Ex : Française": "e.g. French",
  "Ex : Ingénieur logiciel passionné": "e.g. Passionate software engineer",
  "Ex : React, Python, Figma": "e.g. React, Python, Figma",
  "Ex : Tech, Data": "e.g. Tech, Data",
  "Parlez de votre parcours...": "Tell us about your background...",
  "Quelques mots sur votre motivation...": "A few words about your motivation...",
  "Répondre...": "Reply...",
  "Sélectionner un candidat": "Select a candidate",
  "Visio (par défaut Jitsi)": "Video (Jitsi by default)",
  "Votre message...": "Your message...",

  // Toasts
  "Candidature envoyée !": "Application sent!",
  "Candidature supprimée": "Application deleted",
  "Contrat supprimé": "Contract deleted",
  "Couleur personnalisée appliquée": "Custom color applied",
  "Enregistrement démarré": "Recording started",
  "Enregistrement sauvegardé — résumé IA en cours dans « Enregistrements ».": "Recording saved — AI summary in progress in \u201cRecordings\u201d.",
  "Enregistrement supprimé": "Recording deleted",
  "Entretien planifié — le candidat a été notifié.": "Interview scheduled — the candidate has been notified.",
  "Entretien supprimé": "Interview deleted",
  "Offre pré-remplie par l'IA. Vérifiez et ajustez avant de publier.": "Offer pre-filled by AI. Review and adjust before publishing.",
  "Offre supprimée": "Offer deleted",
  "Profil enregistré. L'IA affinera vos recommandations d'offres.": "Profile saved. The AI will refine your offer recommendations.",
  "Statut mis à jour": "Status updated",
  "Utilisateur supprimé": "User deleted",
  "Évaluation enregistrée": "Evaluation saved",
  "Impossible de démarrer l'enregistrement (partage d'écran refusé ?)": "Unable to start recording (screen sharing refused?)",
  "Merci de répondre à toutes les questions.": "Please answer all questions.",
  "Veuillez joindre votre CV.": "Please attach your resume.",

  // --- Interview statuses (Lot 1 & 2) ---
  "Entretien fixé": "Interview scheduled",
  "Entretien fait": "Interview done",
  "Entretiens fixés": "Interviews scheduled",
  "Note d'entretien :": "Interview note:",
  "Note interne :": "Internal note:",

  // --- Users list: filters, badges, panels ---
  "Rechercher (nom, email, domaine, contenu du CV...)": "Search (name, email, domain, CV content...)",
  "Scan CV": "CV scan",
  "CV bien scanné": "CV scanned",
  "CV non scanné": "CV not scanned",

  // --- CV scanning panel ---
  "Scanning IA des CV — extraction structurée (compétences, expériences, formations…)": "AI CV scanning — structured extraction (skills, experience, education…)",
  "Scanner les CV non scannés": "Scan unscanned CVs",
  "Scan en cours…": "Scanning…",
  "Tout re-scanner": "Re-scan all",
  "CV total :": "Total CVs:",
  "Scannés :": "Scanned:",
  "À scanner :": "To scan:",
  "Erreurs :": "Errors:",
  "Scan des CV lancé en arrière-plan.": "CV scan started in the background.",
  "Un scan est déjà en cours.": "A scan is already running.",
  "Impossible de lancer le scan.": "Unable to start the scan.",

  // --- Conversational AI agent ---
  "Agent IA conversationnel — analyse des profils & du contenu des CV": "Conversational AI agent — analyses profiles & CV content",
  "Nouvelle conversation": "New conversation",
  "L'agent analyse les CV…": "The agent is analysing the CVs…",
  "Envoyer": "Send",
  "Voir le profil": "View profile",
  "Parlez maintenant…": "Speak now…",
  "Ex : qui a de l'expérience React et connaît Docker ?": "e.g. who has React experience and knows Docker?",
  "Arrêter la dictée": "Stop dictation",
  "Dicter la requête": "Dictate the query",
  "La saisie vocale n'est pas supportée par ce navigateur (utilisez Chrome ou Edge).": "Voice input is not supported by this browser (use Chrome or Edge).",
  "Recherche IA indisponible": "AI search unavailable",
  "Désolé, la recherche IA est momentanément indisponible.": "Sorry, the AI search is temporarily unavailable.",

  // --- CV preview dialog (structured extraction) ---
  "Chargement…": "Loading…",
  "Données extraites par l'IA": "Data extracted by AI",
  "Poste :": "Position:",
  "Expérience :": "Experience:",
  "Email :": "Email:",
  "Téléphone :": "Phone:",
  "Compétences": "Skills",
  "Expériences": "Experience",
  "Formations": "Education",
  "Langues :": "Languages:",
  "Échec du scan de ce CV. Cliquez « Re-scanner » pour réessayer.": "This CV scan failed. Click \u201cRe-scan\u201d to try again.",
  "Ce CV n'a pas encore été scanné. Cliquez « Re-scanner » pour extraire les données.": "This CV has not been scanned yet. Click \u201cRe-scan\u201d to extract the data.",
  "Texte brut du CV": "Raw CV text",
  "Aucun texte brut exploitable dans ce CV.": "No usable raw text in this CV.",
  "CV original": "Original CV",
  "Re-scanner": "Re-scan",
  "Re-scan du CV en cours…": "Re-scanning CV…",
  "CV re-scanné": "CV re-scanned",
  "Échec du re-scan": "Re-scan failed",
  "Impossible de charger le CV": "Unable to load the CV",
  "Impossible d'ouvrir le CV original": "Unable to open the original CV",
  "Suppression effectuée": "Deletion done",

  // --- Candidate profile dialog: add application ---
  "+ Ajouter une candidature": "+ Add application",
  "— Choisir une offre —": "— Choose an offer —",
  "Créer la candidature": "Create application",
  "Création…": "Creating…",
  "Candidature créée": "Application created",
  "Choisissez une offre": "Choose an offer",
  "Échec de la création": "Creation failed",
  "Modifier": "Edit",
  "Note interne": "Internal note",
  "Note d'entretien": "Interview note",
  "Aucune": "None",
  "Enregistrer": "Save",
  "Enregistrement…": "Saving…",
  "Compte-rendu de l'entretien...": "Interview report...",
  "Candidature mise à jour": "Application updated",
  "Échec de la mise à jour": "Update failed",
  "Inscription": "Registered",
  "Rendez-vous pour": "Appointment for",
  ". Fixez la date et l'heure — il apparaîtra dans son espace et il sera notifié.": ". Set the date and time — it will appear in their space and they will be notified.",
  "Planifier l'entretien": "Schedule the interview",
  "Documents techniques": "Technical documents",
  "Documents techniques du candidat": "Candidate technical documents",
  "Speed Test Internet": "Internet Speed Test",
  "Configuration de l'ordinateur": "Computer configuration",
  "Voir le Speed Test": "View Speed Test",
  "Ajouter des captures": "Add screenshots",
  "Aucun document fourni": "No document provided",
  "fichier(s) fourni(s)": "file(s) provided",
};

export default FR_EN;
